import React, { useState } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Chip,
  Collapse,
  IconButton,
  Divider,
  Grid
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

const AgencyFilters = ({ 
  filters = {}, 
  onFiltersChange, 
  stats = {},
  isCollapsed = false,
  onToggleCollapse 
}) => {
  const [localFilters, setLocalFilters] = useState({
    noKeywords: false,
    noPosts: false,
    poorRankings: false,
    urgentAction: false,
    minKeywordCount: 0,
    maxRankingScore: 20,
    minOptimizationScore: 0,
    ...filters
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      noKeywords: false,
      noPosts: false,
      poorRankings: false,
      urgentAction: false,
      minKeywordCount: 0,
      maxRankingScore: 20,
      minOptimizationScore: 0
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.noKeywords) count++;
    if (localFilters.noPosts) count++;
    if (localFilters.poorRankings) count++;
    if (localFilters.urgentAction) count++;
    if (localFilters.minKeywordCount > 0) count++;
    if (localFilters.maxRankingScore < 20) count++;
    if (localFilters.minOptimizationScore > 0) count++;
    return count;
  };

  const getActiveFiltersChips = () => {
    const chips = [];
    if (localFilters.noKeywords) chips.push('Keine Keywords');
    if (localFilters.noPosts) chips.push('Keine Posts');
    if (localFilters.poorRankings) chips.push('Schlechte Rankings');
    if (localFilters.urgentAction) chips.push('Dringender Handlungsbedarf');
    if (localFilters.minKeywordCount > 0) chips.push(`Min. ${localFilters.minKeywordCount} Keywords`);
    if (localFilters.maxRankingScore < 20) chips.push(`Max. Ranking ${localFilters.maxRankingScore}`);
    if (localFilters.minOptimizationScore > 0) chips.push(`Min. Optimierung ${localFilters.minOptimizationScore}%`);
    return chips;
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      {/* Filter Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: isCollapsed ? 0 : 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6" component="h2">
            Filter
          </Typography>
          {getActiveFiltersCount() > 0 && (
            <Chip
              label={getActiveFiltersCount()}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getActiveFiltersCount() > 0 && (
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleResetFilters}
              color="secondary"
            >
              Zurücksetzen
            </Button>
          )}
          
          <IconButton onClick={onToggleCollapse} size="small">
            {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Active Filters Chips (always visible) */}
      {getActiveFiltersCount() > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {getActiveFiltersChips().map((chip, index) => (
              <Chip
                key={index}
                label={chip}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      <Collapse in={!isCollapsed}>
        <Grid container spacing={3}>
          {/* Quick Filters */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Schnellfilter
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.noKeywords}
                    onChange={(e) => handleFilterChange('noKeywords', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Keine Keywords</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unternehmen ohne erfasste Keywords
                      {stats.customersWithoutKeywords && ` (${stats.customersWithoutKeywords})`}
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.noPosts}
                    onChange={(e) => handleFilterChange('noPosts', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Keine Posts</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unternehmen ohne veröffentlichte Posts
                      {stats.customersWithoutPosts && ` (${stats.customersWithoutPosts})`}
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.poorRankings}
                    onChange={(e) => handleFilterChange('poorRankings', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Schlechte Rankings</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Durchschnittliches Ranking > 10
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localFilters.urgentAction}
                    onChange={(e) => handleFilterChange('urgentAction', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Dringender Handlungsbedarf</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unternehmen mit rotem Status
                      {stats.urgentActionNeeded && ` (${stats.urgentActionNeeded})`}
                    </Typography>
                  </Box>
                }
              />
            </FormGroup>
          </Grid>

          {/* Range Filters */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Bereichsfilter
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Mindestanzahl Keywords
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={localFilters.minKeywordCount}
                  onChange={(e, value) => handleFilterChange('minKeywordCount', value)}
                  min={0}
                  max={20}
                  step={1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20+' }
                  ]}
                  valueLabelDisplay="auto"
                  color="primary"
                />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                Maximales Ranking
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={localFilters.maxRankingScore}
                  onChange={(e, value) => handleFilterChange('maxRankingScore', value)}
                  min={1}
                  max={20}
                  step={1}
                  marks={[
                    { value: 3, label: 'Top 3' },
                    { value: 10, label: 'Top 10' },
                    { value: 20, label: '20+' }
                  ]}
                  valueLabelDisplay="auto"
                  color="primary"
                />
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" gutterBottom>
                Mindest-Optimierungsgrad (%)
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={localFilters.minOptimizationScore}
                  onChange={(e, value) => handleFilterChange('minOptimizationScore', value)}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 50, label: '50%' },
                    { value: 80, label: '80%' },
                    { value: 100, label: '100%' }
                  ]}
                  valueLabelDisplay="auto"
                  color="primary"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Stats Summary */}
        {stats.totalCustomers && (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2,
            p: 2,
            bgcolor: amDesignColors.paper,
            borderRadius: 2
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {stats.totalCustomers}
              </Typography>
              <Typography variant="caption">
                Gesamt
              </Typography>
            </Box>
            
            {stats.customersWithKeywords && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="success.main">
                  {stats.customersWithKeywords}
                </Typography>
                <Typography variant="caption">
                  Mit Keywords
                </Typography>
              </Box>
            )}
            
            {stats.customersWithPosts && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="info.main">
                  {stats.customersWithPosts}
                </Typography>
                <Typography variant="caption">
                  Mit Posts
                </Typography>
              </Box>
            )}
            
            {stats.urgentActionNeeded && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="error.main">
                  {stats.urgentActionNeeded}
                </Typography>
                <Typography variant="caption">
                  Handlungsbedarf
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default AgencyFilters;